import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BarChart3, Mail, Eye, MousePointer, AlertTriangle, RefreshCw, Download, Search, Filter, ChevronLeft, ChevronRight, CheckCircle, XCircle, Clock, Ban, TrendingUp, Users, Link2, ArrowLeft, Zap, ShieldOff, ShieldCheck, Plus, Trash2, UserX } from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

/**
 * EmailAnalytics Component
 * 
 * Displays email delivery analytics for organization emails
 * including open rates, click rates, bounces, spam reports,
 * and campaign-level deep analysis
 */
const EmailAnalytics = ({ organizationId, collectionId = null }) => {
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    delivered: 0,
    opened: 0,
    clicked: 0,
    bounced: 0,
    spam: 0,
    unsubscribed: 0
  });
  
  // View mode: 'log' or 'campaigns'
  const [viewMode, setViewMode] = useState('campaigns');
  
  // Campaign analysis state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [campaignDetailLoading, setCampaignDetailLoading] = useState(false);
  const [campaignDateRange, setCampaignDateRange] = useState('30d');
  const [campaignSearch, setCampaignSearch] = useState('');
  
  // Suppression list state
  const [suppressions, setSuppressions] = useState([]);
  const [suppressionsLoading, setSuppressionsLoading] = useState(false);
  const [problemEmails, setProblemEmails] = useState([]);
  const [problemEmailsLoading, setProblemEmailsLoading] = useState(false);
  const [suppressionSearch, setSuppressionSearch] = useState('');
  const [addSuppressionEmail, setAddSuppressionEmail] = useState('');
  const [addSuppressionReason, setAddSuppressionReason] = useState('manual');
  const [addSuppressionNote, setAddSuppressionNote] = useState('');
  const [suppressionActionLoading, setSuppressActionLoading] = useState(false);
  const [suppressionError, setSuppressionError] = useState(null);
  const [suppressionSuccess, setSuppressionSuccess] = useState(null);
  const [suppressionTableExists, setSuppressionTableExists] = useState(true);
  
  // Filters
  const [dateRange, setDateRange] = useState('7d'); // '24h', '7d', '30d', 'all'
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Fetch email logs
  const fetchEmailLogs = async (showRefreshSpinner = false) => {
    if (showRefreshSpinner) setRefreshing(true);
    else setLoading(true);

    try {
      // Include emails where organization_id matches OR is null (historical data before we added org tracking)
      let query = supabase
        .from('email_logs')
        .select('*', { count: 'exact' })
        .or(`organization_id.eq.${organizationId},organization_id.is.null`)
        .order('sent_at', { ascending: false });

      // Apply collection filter if provided
      if (collectionId) {
        query = query.eq('collection_id', collectionId);
      }

      // Apply date range filter
      const now = new Date();
      if (dateRange === '24h') {
        query = query.gte('sent_at', new Date(now - 24 * 60 * 60 * 1000).toISOString());
      } else if (dateRange === '7d') {
        query = query.gte('sent_at', new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString());
      } else if (dateRange === '30d') {
        query = query.gte('sent_at', new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString());
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      // Apply search
      if (searchQuery) {
        query = query.or(`user_email.ilike.%${searchQuery}%,subject.ilike.%${searchQuery}%`);
      }

      // Apply pagination
      const from = (page - 1) * perPage;
      const to = from + perPage - 1;
      query = query.range(from, to);

      const { data, count, error } = await query;

      if (error) throw error;

      setEmailLogs(data || []);
      setTotalCount(count || 0);

      // Fetch stats separately (without pagination)
      await fetchStats();

    } catch (err) {
      console.error('Error fetching email logs:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch aggregate stats
  const fetchStats = async () => {
    try {
      // Include emails where organization_id matches OR is null (historical data)
      let query = supabase
        .from('email_logs')
        .select('status')
        .or(`organization_id.eq.${organizationId},organization_id.is.null`);

      if (collectionId) {
        query = query.eq('collection_id', collectionId);
      }

      // Apply same date range
      const now = new Date();
      if (dateRange === '24h') {
        query = query.gte('sent_at', new Date(now - 24 * 60 * 60 * 1000).toISOString());
      } else if (dateRange === '7d') {
        query = query.gte('sent_at', new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString());
      } else if (dateRange === '30d') {
        query = query.gte('sent_at', new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      const newStats = {
        total: data?.length || 0,
        sent: data?.filter(d => d.status === 'sent').length || 0,
        delivered: data?.filter(d => d.status === 'delivered').length || 0,
        opened: data?.filter(d => d.status === 'opened').length || 0,
        clicked: data?.filter(d => d.status === 'clicked').length || 0,
        bounced: data?.filter(d => d.status === 'bounced').length || 0,
        spam: data?.filter(d => d.status === 'spam').length || 0,
        unsubscribed: data?.filter(d => d.status === 'unsubscribed').length || 0
      };

      setStats(newStats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Fetch campaign list (distinct subjects with aggregate stats)
  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      // Paginate to fetch ALL rows (Supabase defaults to 1000 limit)
      const PAGE_SIZE = 1000;
      let allData = [];
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        let query = supabase
          .from('email_logs')
          .select('subject, sent_at, status, opened_at, clicked_at, bounced_at, spam_reported_at, unsubscribed_at, open_count, click_count')
          .or(`organization_id.eq.${organizationId},organization_id.is.null`)
          .order('sent_at', { ascending: false })
          .range(from, from + PAGE_SIZE - 1);

        if (collectionId) {
          query = query.eq('collection_id', collectionId);
        }

        // Apply campaign date range
        const now = new Date();
        if (campaignDateRange === '7d') {
          query = query.gte('sent_at', new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString());
        } else if (campaignDateRange === '30d') {
          query = query.gte('sent_at', new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString());
        } else if (campaignDateRange === '90d') {
          query = query.gte('sent_at', new Date(now - 90 * 24 * 60 * 60 * 1000).toISOString());
        }

        const { data, error } = await query;
        if (error) throw error;
        allData = allData.concat(data || []);
        hasMore = (data || []).length === PAGE_SIZE;
        from += PAGE_SIZE;
      }

      const data = allData;

      // Group by subject
      const grouped = {};
      (data || []).forEach(row => {
        const subj = row.subject || '(no subject)';
        if (!grouped[subj]) {
          grouped[subj] = { subject: subj, total: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, spam: 0, unsubscribed: 0, firstSent: row.sent_at, lastSent: row.sent_at };
        }
        const g = grouped[subj];
        g.total++;
        if (['delivered', 'opened', 'clicked'].includes(row.status)) g.delivered++;
        if (row.opened_at) g.opened++;
        if (row.clicked_at) g.clicked++;
        if (row.bounced_at) g.bounced++;
        if (row.spam_reported_at) g.spam++;
        if (row.unsubscribed_at) g.unsubscribed++;
        if (row.sent_at < g.firstSent) g.firstSent = row.sent_at;
        if (row.sent_at > g.lastSent) g.lastSent = row.sent_at;
      });

      const campaignList = Object.values(grouped)
        .map(c => ({
          ...c,
          openRate: c.delivered > 0 ? (c.opened / c.delivered * 100) : 0,
          clickRate: c.delivered > 0 ? (c.clicked / c.delivered * 100) : 0,
          clickToOpenRate: c.opened > 0 ? (c.clicked / c.opened * 100) : 0
        }))
        .sort((a, b) => new Date(b.lastSent) - new Date(a.lastSent));

      setCampaigns(campaignList);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    } finally {
      setCampaignsLoading(false);
    }
  };

  // Fetch detailed campaign analysis for a selected subject
  const fetchCampaignDetail = async (subject) => {
    setCampaignDetailLoading(true);
    try {
      // Paginate to fetch ALL rows for this campaign
      const PAGE_SIZE = 1000;
      let allRows = [];
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from('email_logs')
          .select('*')
          .or(`organization_id.eq.${organizationId},organization_id.is.null`)
          .eq('subject', subject)
          .range(from, from + PAGE_SIZE - 1);

        if (error) throw error;
        allRows = allRows.concat(data || []);
        hasMore = (data || []).length === PAGE_SIZE;
        from += PAGE_SIZE;
      }

      const rows = allRows;
      const total = rows.length;
      const delivered = rows.filter(r => r.delivered_at || ['delivered', 'opened', 'clicked'].includes(r.status)).length;
      const uniqueOpens = rows.filter(r => r.opened_at).length;
      const totalOpens = rows.reduce((sum, r) => sum + (r.open_count || 0), 0);
      const uniqueClicks = rows.filter(r => r.clicked_at).length;
      const totalClicks = rows.reduce((sum, r) => sum + (r.click_count || 0), 0);
      const bounced = rows.filter(r => r.bounced_at).length;
      const spamReports = rows.filter(r => r.spam_reported_at).length;
      const unsubscribed = rows.filter(r => r.unsubscribed_at).length;
      const rejected = rows.filter(r => r.rejected_at).length;
      const firstSent = rows.reduce((min, r) => r.sent_at < min ? r.sent_at : min, rows[0]?.sent_at);
      const lastSent = rows.reduce((max, r) => r.sent_at > max ? r.sent_at : max, rows[0]?.sent_at);

      // Status breakdown
      const statusBreakdown = {};
      rows.forEach(r => {
        const s = r.status || 'sent';
        statusBreakdown[s] = (statusBreakdown[s] || 0) + 1;
      });

      // Bounce details
      const bounceDetails = {};
      rows.filter(r => r.bounced_at).forEach(r => {
        const key = `${r.bounce_type || 'unknown'}|${r.bounce_description || 'unknown'}`;
        bounceDetails[key] = (bounceDetails[key] || 0) + 1;
      });

      // Reject details
      const rejectDetails = {};
      rows.filter(r => r.rejected_at).forEach(r => {
        const key = r.reject_reason || 'unknown';
        rejectDetails[key] = (rejectDetails[key] || 0) + 1;
      });

      // Click URL breakdown
      const clickUrls = {};
      rows.forEach(r => {
        if (r.clicks && Array.isArray(r.clicks)) {
          r.clicks.forEach(click => {
            const url = click.url || 'unknown';
            clickUrls[url] = (clickUrls[url] || 0) + 1;
          });
        }
      });

      // Time-to-open distribution
      const timeToOpen = { '<1h': 0, '1-6h': 0, '6-24h': 0, '1-2d': 0, '2d+': 0 };
      rows.filter(r => r.opened_at && r.sent_at).forEach(r => {
        const hours = (new Date(r.opened_at) - new Date(r.sent_at)) / (1000 * 60 * 60);
        if (hours < 1) timeToOpen['<1h']++;
        else if (hours < 6) timeToOpen['1-6h']++;
        else if (hours < 24) timeToOpen['6-24h']++;
        else if (hours < 48) timeToOpen['1-2d']++;
        else timeToOpen['2d+']++;
      });

      // Time-to-click distribution
      const timeToClick = { '<1h': 0, '1-6h': 0, '6-24h': 0, '1-2d': 0, '2d+': 0 };
      rows.filter(r => r.clicked_at && r.sent_at).forEach(r => {
        const hours = (new Date(r.clicked_at) - new Date(r.sent_at)) / (1000 * 60 * 60);
        if (hours < 1) timeToClick['<1h']++;
        else if (hours < 6) timeToClick['1-6h']++;
        else if (hours < 24) timeToClick['6-24h']++;
        else if (hours < 48) timeToClick['1-2d']++;
        else timeToClick['2d+']++;
      });

      setCampaignDetail({
        subject,
        total,
        delivered,
        uniqueOpens,
        totalOpens,
        uniqueClicks,
        totalClicks,
        bounced,
        spamReports,
        unsubscribed,
        rejected,
        firstSent,
        lastSent,
        deliveryRate: total > 0 ? (delivered / total * 100) : 0,
        openRate: delivered > 0 ? (uniqueOpens / delivered * 100) : 0,
        clickRate: delivered > 0 ? (uniqueClicks / delivered * 100) : 0,
        clickToOpenRate: uniqueOpens > 0 ? (uniqueClicks / uniqueOpens * 100) : 0,
        avgOpensPerOpener: uniqueOpens > 0 ? (totalOpens / uniqueOpens) : 0,
        avgClicksPerClicker: uniqueClicks > 0 ? (totalClicks / uniqueClicks) : 0,
        statusBreakdown,
        bounceDetails,
        rejectDetails,
        clickUrls,
        timeToOpen,
        timeToClick,
        recipients: rows
      });
    } catch (err) {
      console.error('Error fetching campaign detail:', err);
    } finally {
      setCampaignDetailLoading(false);
    }
  };

  // Fetch active suppressions from email_suppressions table
  const fetchSuppressions = useCallback(async () => {
    setSuppressionsLoading(true);
    setSuppressionError(null);
    try {
      const { data, error } = await supabase
        .from('email_suppressions')
        .select('*')
        .eq('is_active', true)
        .or(`organization_id.eq.${organizationId},organization_id.is.null`)
        .order('suppressed_at', { ascending: false });

      if (error) {
        // Table might not exist yet
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          setSuppressionTableExists(false);
          setSuppressions([]);
          return;
        }
        throw error;
      }
      setSuppressionTableExists(true);
      setSuppressions(data || []);
    } catch (err) {
      console.error('Error fetching suppressions:', err);
      if (err.code === '42P01' || err.message?.includes('does not exist')) {
        setSuppressionTableExists(false);
      }
    } finally {
      setSuppressionsLoading(false);
    }
  }, [organizationId]);

  // Detect problem emails from email_logs (works even without suppression table)
  const fetchProblemEmails = useCallback(async () => {
    setProblemEmailsLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_logs')
        .select('user_email, user_uuid, bounced_at, bounce_type, bounce_description, rejected_at, reject_reason, spam_reported_at, unsubscribed_at, sent_at')
        .or(`organization_id.eq.${organizationId},organization_id.is.null`)
        .or('bounced_at.not.is.null,rejected_at.not.is.null,spam_reported_at.not.is.null,unsubscribed_at.not.is.null');

      if (error) throw error;

      // Group by email, find the worst issue for each
      const byEmail = {};
      (data || []).forEach(row => {
        const email = row.user_email;
        if (!byEmail[email]) {
          byEmail[email] = { email, user_uuid: row.user_uuid, issues: [], lastSent: row.sent_at, sendCount: 0 };
        }
        const e = byEmail[email];
        if (row.sent_at > e.lastSent) e.lastSent = row.sent_at;
        e.sendCount++;
        if (row.bounced_at) e.issues.push({ type: 'bounce', subtype: row.bounce_type, desc: row.bounce_description, at: row.bounced_at });
        if (row.rejected_at) e.issues.push({ type: 'rejected', desc: row.reject_reason, at: row.rejected_at });
        if (row.spam_reported_at) e.issues.push({ type: 'spam', at: row.spam_reported_at });
        if (row.unsubscribed_at) e.issues.push({ type: 'unsubscribed', at: row.unsubscribed_at });
      });

      // Sort by severity: spam > hard_bounce > rejected > soft_bounce > unsubscribed
      const severityOrder = { spam: 0, hard_bounce: 1, rejected: 2, soft_bounce: 3, unsubscribed: 4, bounce: 5 };
      const list = Object.values(byEmail).map(e => {
        const worst = e.issues.sort((a, b) => {
          const aKey = a.subtype || a.type;
          const bKey = b.subtype || b.type;
          return (severityOrder[aKey] ?? 99) - (severityOrder[bKey] ?? 99);
        })[0];
        return { ...e, worstIssue: worst, issueCount: e.issues.length };
      }).sort((a, b) => {
        const aKey = a.worstIssue?.subtype || a.worstIssue?.type;
        const bKey = b.worstIssue?.subtype || b.worstIssue?.type;
        return (severityOrder[aKey] ?? 99) - (severityOrder[bKey] ?? 99);
      });

      setProblemEmails(list);
    } catch (err) {
      console.error('Error fetching problem emails:', err);
    } finally {
      setProblemEmailsLoading(false);
    }
  }, [organizationId]);

  // Add email to suppression list
  const addSuppression = async (email, reason = 'manual', details = '', userUuid = null) => {
    if (!suppressionTableExists) {
      setSuppressionError('Suppression table not yet created. Please run the migration SQL first.');
      return false;
    }
    setSuppressActionLoading(true);
    setSuppressionError(null);
    setSuppressionSuccess(null);
    try {
      // Check if already suppressed
      const existing = suppressions.find(s => s.email.toLowerCase() === email.toLowerCase());
      if (existing) {
        setSuppressionError(`${email} is already on the suppression list.`);
        return false;
      }

      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('email_suppressions')
        .insert({
          email: email.toLowerCase().trim(),
          user_uuid: userUuid || null,
          reason,
          source: 'admin',
          details: details || null,
          suppressed_by: user?.id || null,
          organization_id: organizationId
        });

      if (error) throw error;

      setSuppressionSuccess(`${email} added to suppression list.`);
      setAddSuppressionEmail('');
      setAddSuppressionNote('');
      await fetchSuppressions();
      return true;
    } catch (err) {
      console.error('Error adding suppression:', err);
      setSuppressionError(`Failed to add: ${err.message}`);
      return false;
    } finally {
      setSuppressActionLoading(false);
    }
  };

  // Remove (reactivate) suppression
  const removeSuppression = async (suppressionId) => {
    setSuppressActionLoading(true);
    setSuppressionError(null);
    setSuppressionSuccess(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('email_suppressions')
        .update({
          is_active: false,
          reactivated_at: new Date().toISOString(),
          reactivated_by: user?.id || null
        })
        .eq('id', suppressionId);

      if (error) throw error;

      setSuppressionSuccess('Email removed from suppression list.');
      await fetchSuppressions();
    } catch (err) {
      console.error('Error removing suppression:', err);
      setSuppressionError(`Failed to remove: ${err.message}`);
    } finally {
      setSuppressActionLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) {
      fetchEmailLogs();
    }
  }, [organizationId, collectionId, dateRange, statusFilter, searchQuery, page, perPage]);

  // Fetch campaigns when view mode changes or filters change
  useEffect(() => {
    if (organizationId && viewMode === 'campaigns') {
      fetchCampaigns();
    }
  }, [organizationId, collectionId, viewMode, campaignDateRange]);

  // Fetch suppressions and problem emails when switching to suppressions view
  useEffect(() => {
    if (organizationId && viewMode === 'suppressions') {
      fetchSuppressions();
      fetchProblemEmails();
    }
  }, [organizationId, viewMode, fetchSuppressions, fetchProblemEmails]);

  // Filter campaigns by search
  const filteredCampaigns = useMemo(() => {
    if (!campaignSearch) return campaigns;
    const q = campaignSearch.toLowerCase();
    return campaigns.filter(c => c.subject.toLowerCase().includes(q));
  }, [campaigns, campaignSearch]);

  // Filtered suppressions
  const filteredSuppressions = useMemo(() => {
    if (!suppressionSearch) return suppressions;
    const q = suppressionSearch.toLowerCase();
    return suppressions.filter(s => s.email.toLowerCase().includes(q) || s.reason?.toLowerCase().includes(q));
  }, [suppressions, suppressionSearch]);

  // Suppressed emails set for quick lookup
  const suppressedEmailSet = useMemo(() => {
    return new Set(suppressions.map(s => s.email.toLowerCase()));
  }, [suppressions]);

  // Problem emails not yet suppressed
  const unsuppressedProblemEmails = useMemo(() => {
    return problemEmails.filter(p => !suppressedEmailSet.has(p.email.toLowerCase()));
  }, [problemEmails, suppressedEmailSet]);

  // Status badge component
  const StatusBadge = ({ status }) => {
    const config = {
      sent: { bg: '#dbeafe', color: '#1e40af', icon: Clock, label: 'Sent' },
      delivered: { bg: '#dcfce7', color: '#166534', icon: CheckCircle, label: 'Delivered' },
      opened: { bg: '#e0e7ff', color: '#4338ca', icon: Eye, label: 'Opened' },
      clicked: { bg: '#fae8ff', color: '#a21caf', icon: MousePointer, label: 'Clicked' },
      bounced: { bg: '#fee2e2', color: '#991b1b', icon: XCircle, label: 'Bounced' },
      spam: { bg: '#fef3c7', color: '#92400e', icon: AlertTriangle, label: 'Spam' },
      unsubscribed: { bg: '#f3f4f6', color: '#374151', icon: Ban, label: 'Unsubscribed' },
      rejected: { bg: '#fee2e2', color: '#991b1b', icon: XCircle, label: 'Rejected' }
    };

    const c = config[status] || config.sent;
    const Icon = c.icon;

    return (
      <span style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: '0.25rem', 
        padding: '0.25rem 0.5rem', 
        borderRadius: '9999px',
        backgroundColor: c.bg,
        color: c.color,
        fontSize: '0.75rem',
        fontWeight: '500'
      }}>
        <Icon style={{ height: '0.75rem', width: '0.75rem' }} />
        {c.label}
      </span>
    );
  };

  // Calculate rates
  const deliveryRate = stats.total > 0 ? ((stats.delivered + stats.opened + stats.clicked) / stats.total * 100).toFixed(1) : 0;
  const openRate = (stats.delivered + stats.opened + stats.clicked) > 0 ? ((stats.opened + stats.clicked) / (stats.delivered + stats.opened + stats.clicked) * 100).toFixed(1) : 0;
  const clickRate = (stats.opened + stats.clicked) > 0 ? (stats.clicked / (stats.opened + stats.clicked) * 100).toFixed(1) : 0;
  const bounceRate = stats.total > 0 ? (stats.bounced / stats.total * 100).toFixed(1) : 0;

  const totalPages = Math.ceil(totalCount / perPage);

  if (loading && !refreshing) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <RefreshCw style={{ height: '2rem', width: '2rem', color: '#7c3aed', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>Loading analytics...</p>
      </div>
    );
  }

  // Helper: horizontal bar for visual distribution
  const DistributionBar = ({ label, value, total, color = '#7c3aed' }) => {
    const pct = total > 0 ? (value / total * 100) : 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div style={{ width: '80px', fontSize: '0.8rem', color: '#374151', textAlign: 'right', flexShrink: 0 }}>{label}</div>
        <div style={{ flex: 1, backgroundColor: '#f3f4f6', borderRadius: '4px', height: '22px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: '4px', transition: 'width 0.3s ease', minWidth: value > 0 ? '2px' : '0' }}></div>
        </div>
        <div style={{ width: '60px', fontSize: '0.8rem', color: '#6b7280', flexShrink: 0 }}>{value} ({pct.toFixed(0)}%)</div>
      </div>
    );
  };

  // Campaign Detail View
  if (viewMode === 'campaigns' && selectedCampaign && campaignDetail) {
    const d = campaignDetail;
    const statusOrder = ['clicked', 'opened', 'delivered', 'sent', 'bounced', 'rejected', 'spam', 'unsubscribed'];
    const statusColors = {
      sent: '#3b82f6', delivered: '#22c55e', opened: '#6366f1', clicked: '#a855f7',
      bounced: '#ef4444', rejected: '#ef4444', spam: '#f59e0b', unsubscribed: '#6b7280'
    };

    return (
      <div>
        {/* Back button */}
        <button
          onClick={() => { setSelectedCampaign(null); setCampaignDetail(null); }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0', background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', fontSize: '0.875rem', fontWeight: '500', marginBottom: '1rem' }}
        >
          <ArrowLeft style={{ height: '1rem', width: '1rem' }} />
          Back to Campaigns
        </button>

        {/* Campaign title */}
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', color: '#111827', margin: '0 0 0.25rem 0' }}>"{d.subject}"</h3>
          <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>
            Sent {new Date(d.firstSent).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
            {d.firstSent !== d.lastSent && ` — ${new Date(d.lastSent).toLocaleDateString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
          </p>
        </div>

        {/* Key metrics row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
          {[
            { label: 'Recipients', value: d.total, color: '#374151', bg: '#f3f4f6' },
            { label: 'Delivered', value: d.delivered, sub: `${d.deliveryRate.toFixed(1)}%`, color: '#166534', bg: '#dcfce7' },
            { label: 'Unique Opens', value: d.uniqueOpens, sub: `${d.openRate.toFixed(1)}%`, color: '#4338ca', bg: '#e0e7ff' },
            { label: 'Unique Clicks', value: d.uniqueClicks, sub: `${d.clickRate.toFixed(1)}%`, color: '#a21caf', bg: '#fae8ff' },
            { label: 'Click-to-Open', value: `${d.clickToOpenRate.toFixed(1)}%`, color: '#7c2d12', bg: '#fff7ed' },
            { label: 'Bounced', value: d.bounced, sub: d.total > 0 ? `${(d.bounced/d.total*100).toFixed(1)}%` : '0%', color: '#991b1b', bg: '#fee2e2' },
          ].map((m, i) => (
            <div key={i} style={{ backgroundColor: m.bg, borderRadius: '0.5rem', padding: '0.875rem', textAlign: 'center' }}>
              <div style={{ fontSize: '1.375rem', fontWeight: '700', color: m.color }}>{m.value}</div>
              <div style={{ fontSize: '0.7rem', color: m.color, opacity: 0.8 }}>{m.label}</div>
              {m.sub && <div style={{ fontSize: '0.7rem', color: m.color, opacity: 0.6, marginTop: '2px' }}>{m.sub}</div>}
            </div>
          ))}
        </div>

        {/* Engagement metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Avg Opens per Opener</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#4338ca' }}>{d.avgOpensPerOpener.toFixed(2)}</div>
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Avg Clicks per Clicker</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#a21caf' }}>{d.avgClicksPerClicker.toFixed(2)}</div>
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Spam Reports</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: d.spamReports > 0 ? '#dc2626' : '#22c55e' }}>{d.spamReports}</div>
          </div>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.25rem' }}>Unsubscribes</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: d.unsubscribed > 0 ? '#dc2626' : '#22c55e' }}>{d.unsubscribed}</div>
          </div>
        </div>

        {/* Two column: Status Breakdown + Time-to-Open */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {/* Status Breakdown */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', margin: '0 0 0.75rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BarChart3 style={{ height: '0.875rem', width: '0.875rem' }} />
              Status Breakdown
            </h4>
            {statusOrder.filter(s => d.statusBreakdown[s]).map(s => (
              <DistributionBar key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} value={d.statusBreakdown[s]} total={d.total} color={statusColors[s] || '#6b7280'} />
            ))}
          </div>

          {/* Time to Open */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', margin: '0 0 0.75rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock style={{ height: '0.875rem', width: '0.875rem' }} />
              Time to First Open
            </h4>
            {Object.entries(d.timeToOpen).filter(([, v]) => v > 0).length === 0 ? (
              <p style={{ fontSize: '0.8rem', color: '#9ca3af', margin: 0 }}>No opens recorded yet</p>
            ) : (
              <>
                {[
                  { key: '<1h', label: '< 1 hour' },
                  { key: '1-6h', label: '1–6 hours' },
                  { key: '6-24h', label: '6–24 hours' },
                  { key: '1-2d', label: '1–2 days' },
                  { key: '2d+', label: '2+ days' },
                ].filter(b => d.timeToOpen[b.key] > 0).map(b => (
                  <DistributionBar key={b.key} label={b.label} value={d.timeToOpen[b.key]} total={d.uniqueOpens} color="#6366f1" />
                ))}
              </>
            )}
          </div>
        </div>

        {/* Click URLs */}
        {Object.keys(d.clickUrls).length > 0 && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', margin: '0 0 0.75rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Link2 style={{ height: '0.875rem', width: '0.875rem' }} />
              Clicked URLs
            </h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>URL</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', width: '80px' }}>Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(d.clickUrls).sort((a, b) => b[1] - a[1]).map(([url, count], i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '0.5rem 0.75rem', color: '#6366f1', wordBreak: 'break-all' }}>
                        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#6366f1', textDecoration: 'none' }}>{url}</a>
                      </td>
                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: '600', color: '#374151' }}>{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bounce & Reject Details */}
        {(Object.keys(d.bounceDetails).length > 0 || Object.keys(d.rejectDetails).length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {Object.keys(d.bounceDetails).length > 0 && (
              <div style={{ border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '1rem', backgroundColor: '#fef2f2' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#991b1b', margin: '0 0 0.75rem 0' }}>Bounce Details</h4>
                {Object.entries(d.bounceDetails).sort((a, b) => b[1] - a[1]).map(([key, count], i) => {
                  const [type, desc] = key.split('|');
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.375rem 0', borderBottom: i < Object.keys(d.bounceDetails).length - 1 ? '1px solid #fecaca' : 'none', fontSize: '0.8rem' }}>
                      <span style={{ color: '#991b1b' }}>{type.replace(/_/g, ' ')} — {desc.replace(/_/g, ' ')}</span>
                      <span style={{ fontWeight: '600', color: '#991b1b' }}>{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {Object.keys(d.rejectDetails).length > 0 && (
              <div style={{ border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '1rem', backgroundColor: '#fef2f2' }}>
                <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#991b1b', margin: '0 0 0.75rem 0' }}>Rejection Details</h4>
                {Object.entries(d.rejectDetails).sort((a, b) => b[1] - a[1]).map(([reason, count], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.375rem 0', fontSize: '0.8rem' }}>
                    <span style={{ color: '#991b1b' }}>{reason}</span>
                    <span style={{ fontWeight: '600', color: '#991b1b' }}>{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Effectiveness Assessment */}
        <div style={{ border: '1px solid #bbf7d0', borderRadius: '0.5rem', padding: '1rem', backgroundColor: '#f0fdf4', marginBottom: '1.5rem' }}>
          <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#166534', margin: '0 0 0.75rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <TrendingUp style={{ height: '0.875rem', width: '0.875rem' }} />
            Campaign Performance Summary
          </h4>
          <div style={{ fontSize: '0.8rem', color: '#374151', lineHeight: 1.6 }}>
            {d.openRate >= 40 ? (
              <p style={{ margin: '0 0 0.5rem 0' }}>✅ <strong>Open rate ({d.openRate.toFixed(1)}%)</strong> is excellent — well above the industry average of 25-28% for arts/nonprofits.</p>
            ) : d.openRate >= 25 ? (
              <p style={{ margin: '0 0 0.5rem 0' }}>👍 <strong>Open rate ({d.openRate.toFixed(1)}%)</strong> is at or above industry average (25-28%) for arts/nonprofits.</p>
            ) : (
              <p style={{ margin: '0 0 0.5rem 0' }}>⚠️ <strong>Open rate ({d.openRate.toFixed(1)}%)</strong> is below industry average (25-28%). Consider testing different subject lines.</p>
            )}
            {d.clickRate >= 5 ? (
              <p style={{ margin: '0 0 0.5rem 0' }}>✅ <strong>Click rate ({d.clickRate.toFixed(1)}%)</strong> is strong — well above the typical 2-3% for the sector.</p>
            ) : d.clickRate >= 2 ? (
              <p style={{ margin: '0 0 0.5rem 0' }}>👍 <strong>Click rate ({d.clickRate.toFixed(1)}%)</strong> is in line with industry averages (2-3%).</p>
            ) : d.uniqueClicks === 0 ? (
              <p style={{ margin: '0 0 0.5rem 0' }}>ℹ️ <strong>No clicks recorded.</strong> This is normal if the email had no CTAs or the CTA wasn't tracked.</p>
            ) : (
              <p style={{ margin: '0 0 0.5rem 0' }}>⚠️ <strong>Click rate ({d.clickRate.toFixed(1)}%)</strong> is below average. Consider a clearer call-to-action.</p>
            )}
            {d.spamReports === 0 && d.unsubscribed === 0 ? (
              <p style={{ margin: '0 0 0.5rem 0' }}>✅ <strong>Zero spam reports and zero unsubscribes</strong> — the audience found this email relevant.</p>
            ) : (
              <p style={{ margin: '0 0 0.5rem 0' }}>⚠️ <strong>{d.spamReports} spam report(s), {d.unsubscribed} unsubscribe(s)</strong> — review content and targeting.</p>
            )}
            {d.deliveryRate >= 97 ? (
              <p style={{ margin: 0 }}>✅ <strong>Deliverability ({d.deliveryRate.toFixed(1)}%)</strong> is healthy.{d.bounced > 0 ? ` Consider removing the ${d.bounced} bounced address(es) from future sends.` : ''}</p>
            ) : (
              <p style={{ margin: 0 }}>⚠️ <strong>Deliverability ({d.deliveryRate.toFixed(1)}%)</strong> could be improved. Review bounced and rejected addresses.</p>
            )}
          </div>
        </div>

        {/* Individual Recipients Table */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
          <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', margin: 0 }}>
              Individual Recipients ({d.recipients.length})
            </h4>
          </div>
          <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f9fafb' }}>
                <tr>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Recipient</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Opens</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Clicks</th>
                  <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Opened At</th>
                </tr>
              </thead>
              <tbody>
                {d.recipients.sort((a, b) => (b.open_count || 0) - (a.open_count || 0)).map((r, i) => (
                  <tr key={r.id || i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.5rem 0.75rem', color: '#111827' }}>{r.user_email}</td>
                    <td style={{ padding: '0.5rem 0.75rem' }}><StatusBadge status={r.status || 'sent'} /></td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                      {(r.open_count || 0) > 0 ? <span style={{ color: '#4338ca', fontWeight: '500' }}>{r.open_count}</span> : <span style={{ color: '#d1d5db' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                      {(r.click_count || 0) > 0 ? <span style={{ color: '#a21caf', fontWeight: '500' }}>{r.click_count}</span> : <span style={{ color: '#d1d5db' }}>—</span>}
                    </td>
                    <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>
                      {r.opened_at ? new Date(r.opened_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // View mode toggle component for reuse
  const ViewModeToggle = () => (
    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
      {['campaigns', 'log', 'suppressions'].map(mode => {
        const isActive = viewMode === mode;
        const labels = {
          campaigns: { icon: TrendingUp, label: 'Campaign Analysis' },
          log: { icon: Mail, label: 'Email Log' },
          suppressions: { icon: ShieldOff, label: 'Suppression List' }
        };
        const { icon: Icon, label } = labels[mode];
        return (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            style={{
              padding: '0.5rem 1rem', borderRadius: '0.5rem',
              border: isActive ? '1px solid #7c3aed' : '1px solid #d1d5db',
              backgroundColor: isActive ? '#7c3aed' : 'white',
              color: isActive ? 'white' : '#374151',
              fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              position: 'relative'
            }}
          >
            <Icon style={{ height: '0.875rem', width: '0.875rem' }} />
            {label}
            {mode === 'suppressions' && unsuppressedProblemEmails.length > 0 && !isActive && (
              <span style={{
                position: 'absolute', top: '-6px', right: '-6px',
                backgroundColor: '#ef4444', color: 'white', fontSize: '0.65rem',
                fontWeight: '700', borderRadius: '9999px', minWidth: '18px', height: '18px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px'
              }}>{unsuppressedProblemEmails.length}</span>
            )}
          </button>
        );
      })}
    </div>
  );

  // Suppressions View
  if (viewMode === 'suppressions') {
    const issueBadge = (issue) => {
      const configs = {
        hard_bounce: { bg: '#fee2e2', color: '#991b1b', label: 'Hard Bounce' },
        soft_bounce: { bg: '#fef3c7', color: '#92400e', label: 'Soft Bounce' },
        bounce: { bg: '#fee2e2', color: '#991b1b', label: 'Bounce' },
        rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
        spam: { bg: '#fef3c7', color: '#92400e', label: 'Spam Report' },
        unsubscribed: { bg: '#f3f4f6', color: '#374151', label: 'Unsubscribed' },
        manual: { bg: '#e0e7ff', color: '#4338ca', label: 'Manual' },
        spam_report: { bg: '#fef3c7', color: '#92400e', label: 'Spam Report' },
        unsubscribe: { bg: '#f3f4f6', color: '#374151', label: 'Unsubscribed' }
      };
      const key = issue?.subtype || issue?.type || issue;
      const c = configs[key] || { bg: '#f3f4f6', color: '#374151', label: key || 'Unknown' };
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0.2rem 0.5rem', borderRadius: '9999px', backgroundColor: c.bg, color: c.color, fontSize: '0.7rem', fontWeight: '500' }}>
          {c.label}
        </span>
      );
    };

    return (
      <div>
        <ViewModeToggle />

        {/* Suppression stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <div style={{ backgroundColor: '#fee2e2', borderRadius: '0.5rem', padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#991b1b' }}>{suppressionTableExists ? suppressions.length : '—'}</div>
            <div style={{ fontSize: '0.75rem', color: '#991b1b' }}>Suppressed Emails</div>
          </div>
          <div style={{ backgroundColor: '#fef3c7', borderRadius: '0.5rem', padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#92400e' }}>{problemEmails.length}</div>
            <div style={{ fontSize: '0.75rem', color: '#92400e' }}>Problem Addresses</div>
          </div>
          <div style={{ backgroundColor: unsuppressedProblemEmails.length > 0 ? '#fef3c7' : '#dcfce7', borderRadius: '0.5rem', padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: unsuppressedProblemEmails.length > 0 ? '#92400e' : '#166534' }}>{unsuppressedProblemEmails.length}</div>
            <div style={{ fontSize: '0.75rem', color: unsuppressedProblemEmails.length > 0 ? '#92400e' : '#166534' }}>Need Attention</div>
          </div>
        </div>

        {/* Status/error messages */}
        {suppressionError && (
          <div style={{ padding: '0.75rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#991b1b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{suppressionError}</span>
            <button onClick={() => setSuppressionError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', fontWeight: '600' }}>×</button>
          </div>
        )}
        {suppressionSuccess && (
          <div style={{ padding: '0.75rem', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.5rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#166534', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{suppressionSuccess}</span>
            <button onClick={() => setSuppressionSuccess(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#166534', fontWeight: '600' }}>×</button>
          </div>
        )}

        {!suppressionTableExists && (
          <div style={{ padding: '1rem', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '0.5rem', marginBottom: '1.5rem' }}>
            <p style={{ fontSize: '0.875rem', color: '#92400e', margin: '0 0 0.5rem 0', fontWeight: '600' }}>⚠️ Suppression table not yet created</p>
            <p style={{ fontSize: '0.8rem', color: '#92400e', margin: 0 }}>The email_suppressions table needs to be created in Supabase. Run the migration SQL in the SQL Editor first. You can still see detected problem emails below.</p>
          </div>
        )}

        {/* Add suppression manually */}
        {suppressionTableExists && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem', backgroundColor: '#fafafa' }}>
            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', margin: '0 0 0.75rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Plus style={{ height: '0.875rem', width: '0.875rem' }} />
              Suppress an Email Address
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 200px', minWidth: '150px' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.25rem' }}>Email</label>
                <input
                  type="email"
                  value={addSuppressionEmail}
                  onChange={(e) => setAddSuppressionEmail(e.target.value)}
                  placeholder="email@example.com"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #d1d5db', fontSize: '0.875rem', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ width: '140px' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.25rem' }}>Reason</label>
                <select
                  value={addSuppressionReason}
                  onChange={(e) => setAddSuppressionReason(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
                >
                  <option value="manual">Manual</option>
                  <option value="unsubscribe">Unsubscribe</option>
                  <option value="hard_bounce">Hard Bounce</option>
                  <option value="spam_report">Spam Report</option>
                </select>
              </div>
              <div style={{ flex: '1 1 150px', minWidth: '100px' }}>
                <label style={{ display: 'block', fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.25rem' }}>Note (optional)</label>
                <input
                  type="text"
                  value={addSuppressionNote}
                  onChange={(e) => setAddSuppressionNote(e.target.value)}
                  placeholder="Requested via email..."
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid #d1d5db', fontSize: '0.875rem', boxSizing: 'border-box' }}
                />
              </div>
              <button
                onClick={() => addSuppression(addSuppressionEmail, addSuppressionReason, addSuppressionNote)}
                disabled={!addSuppressionEmail.trim() || suppressionActionLoading}
                style={{ padding: '0.5rem 1rem', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: (!addSuppressionEmail.trim() || suppressionActionLoading) ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: '500', opacity: (!addSuppressionEmail.trim() || suppressionActionLoading) ? 0.5 : 1, whiteSpace: 'nowrap' }}
              >
                <ShieldOff style={{ height: '0.875rem', width: '0.875rem', display: 'inline', verticalAlign: 'middle', marginRight: '0.25rem' }} />
                Suppress
              </button>
            </div>
          </div>
        )}

        {/* Detected Problem Emails (from email_logs) */}
        {unsuppressedProblemEmails.length > 0 && (
          <div style={{ border: '1px solid #fecaca', borderRadius: '0.5rem', marginBottom: '1.5rem', overflow: 'hidden' }}>
            <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fef2f2', borderBottom: '1px solid #fecaca', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#991b1b', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertTriangle style={{ height: '0.875rem', width: '0.875rem' }} />
                Detected Problem Emails ({unsuppressedProblemEmails.length})
              </h4>
              {suppressionTableExists && (
                <button
                  onClick={async () => {
                    const hardBounces = unsuppressedProblemEmails.filter(p => p.worstIssue?.subtype === 'hard_bounce' || p.worstIssue?.type === 'spam');
                    if (hardBounces.length === 0) return;
                    if (!window.confirm(`Suppress ${hardBounces.length} hard-bounced and spam-reported email(s)?`)) return;
                    for (const p of hardBounces) {
                      await addSuppression(p.email, p.worstIssue?.subtype || p.worstIssue?.type, `Auto-detected: ${p.worstIssue?.desc || p.worstIssue?.type}`, p.user_uuid);
                    }
                  }}
                  disabled={suppressionActionLoading}
                  style={{ padding: '0.375rem 0.75rem', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '500' }}
                >
                  Suppress All Hard Bounces
                </button>
              )}
            </div>
            <div style={{ overflowX: 'auto', maxHeight: '350px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fef2f2' }}>
                  <tr>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#991b1b', borderBottom: '1px solid #fecaca' }}>Email</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#991b1b', borderBottom: '1px solid #fecaca' }}>Issue</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#991b1b', borderBottom: '1px solid #fecaca' }}>Details</th>
                    <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: '600', color: '#991b1b', borderBottom: '1px solid #fecaca' }}>Times</th>
                    {suppressionTableExists && <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: '600', color: '#991b1b', borderBottom: '1px solid #fecaca', width: '80px' }}>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {unsuppressedProblemEmails.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #fef2f2' }}>
                      <td style={{ padding: '0.5rem 0.75rem', color: '#111827', fontWeight: '500' }}>{p.email}</td>
                      <td style={{ padding: '0.5rem 0.75rem' }}>{issueBadge(p.worstIssue)}</td>
                      <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280', fontSize: '0.75rem' }}>{p.worstIssue?.desc?.replace(/_/g, ' ') || '—'}</td>
                      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: '#6b7280' }}>{p.issueCount}</td>
                      {suppressionTableExists && (
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                          <button
                            onClick={() => addSuppression(p.email, p.worstIssue?.subtype || p.worstIssue?.type || 'manual', `Auto-detected: ${p.worstIssue?.desc || p.worstIssue?.type}`, p.user_uuid)}
                            disabled={suppressionActionLoading}
                            style={{ padding: '0.25rem 0.5rem', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.7rem' }}
                            title="Add to suppression list"
                          >
                            Suppress
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Active Suppression List */}
        {suppressionTableExists && (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
            <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <ShieldOff style={{ height: '0.875rem', width: '0.875rem' }} />
                Active Suppression List ({filteredSuppressions.length})
              </h4>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <div style={{ position: 'relative' }}>
                  <Search style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', height: '0.8rem', width: '0.8rem', color: '#9ca3af' }} />
                  <input
                    type="text"
                    value={suppressionSearch}
                    onChange={(e) => setSuppressionSearch(e.target.value)}
                    placeholder="Search..."
                    style={{ padding: '0.375rem 0.5rem 0.375rem 2rem', borderRadius: '0.375rem', border: '1px solid #d1d5db', fontSize: '0.8rem', width: '180px' }}
                  />
                </div>
                <button
                  onClick={fetchSuppressions}
                  disabled={suppressionsLoading}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer', fontSize: '0.8rem' }}
                >
                  <RefreshCw style={{ height: '0.8rem', width: '0.8rem', animation: suppressionsLoading ? 'spin 1s linear infinite' : 'none' }} />
                </button>
              </div>
            </div>

            {suppressionsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <RefreshCw style={{ height: '1.5rem', width: '1.5rem', color: '#7c3aed', animation: 'spin 1s linear infinite' }} />
              </div>
            ) : filteredSuppressions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                <ShieldCheck style={{ height: '2rem', width: '2rem', color: '#22c55e', marginBottom: '0.5rem' }} />
                <p style={{ margin: 0 }}>{suppressionSearch ? 'No matching suppressions found.' : 'No emails suppressed yet. Clean list!'}</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f9fafb' }}>
                    <tr>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Email</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Reason</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Details</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Suppressed</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', width: '80px' }}>Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSuppressions.map((s) => (
                      <tr key={s.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '0.5rem 0.75rem', color: '#111827', fontWeight: '500' }}>{s.email}</td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>{issueBadge(s.reason)}</td>
                        <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280', fontSize: '0.75rem', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.details || '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', color: '#6b7280' }}>
                          {new Date(s.suppressed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                          <button
                            onClick={() => {
                              if (window.confirm(`Remove ${s.email} from the suppression list? They will receive future emails.`)) {
                                removeSuppression(s.id);
                              }
                            }}
                            disabled={suppressionActionLoading}
                            style={{ padding: '0.25rem 0.5rem', backgroundColor: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '0.25rem', cursor: 'pointer', fontSize: '0.7rem' }}
                            title="Remove from suppression list"
                          >
                            <ShieldCheck style={{ height: '0.75rem', width: '0.75rem' }} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Info tip */}
        <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f0fdf4', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
          <p style={{ fontSize: '0.8rem', color: '#166534', margin: 0 }}>
            <strong>How it works:</strong> Problem emails are auto-detected from bounces, rejections, and spam reports in your email logs. 
            Suppressed emails will be skipped when sending future campaigns. Mandrill also maintains its own rejection list — 
            emails that previously hard-bounced are blocked by Mandrill automatically.
          </p>
        </div>
      </div>
    );
  }

  // Campaign List View
  if (viewMode === 'campaigns') {
    return (
      <div>
        <ViewModeToggle />

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem', alignItems: 'center' }}>
          <select
            value={campaignDateRange}
            onChange={(e) => setCampaignDateRange(e.target.value)}
            style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>

          <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '150px', maxWidth: '300px' }}>
            <Search style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', height: '1rem', width: '1rem', color: '#9ca3af' }} />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={campaignSearch}
              onChange={(e) => setCampaignSearch(e.target.value)}
              style={{ width: '100%', padding: '0.5rem 1rem 0.5rem 2.5rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', fontSize: '0.875rem', boxSizing: 'border-box' }}
            />
          </div>

          <button
            onClick={() => fetchCampaigns()}
            disabled={campaignsLoading}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: campaignsLoading ? 'not-allowed' : 'pointer', fontSize: '0.875rem' }}
          >
            <RefreshCw style={{ height: '1rem', width: '1rem', animation: campaignsLoading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>

        {campaignsLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <RefreshCw style={{ height: '2rem', width: '2rem', color: '#7c3aed', animation: 'spin 1s linear infinite' }} />
            <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>Loading campaigns...</p>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
            <Mail style={{ height: '2rem', width: '2rem', color: '#d1d5db', marginBottom: '0.5rem' }} />
            <p>No campaigns found for the selected period.</p>
          </div>
        ) : (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Campaign Subject</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', width: '60px' }}>Sent</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', width: '80px' }}>Open %</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', width: '80px' }}>Click %</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', width: '80px' }}>CTO %</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb', width: '90px' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredCampaigns.map((c, i) => (
                  <tr
                    key={i}
                    onClick={() => { setSelectedCampaign(c.subject); fetchCampaignDetail(c.subject); }}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', transition: 'background-color 0.15s' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f3ff'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '0.75rem 1rem', color: '#111827', fontWeight: '500', maxWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.subject}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center', color: '#374151' }}>{c.total}</td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>
                      <span style={{ 
                        color: c.openRate >= 40 ? '#166534' : c.openRate >= 25 ? '#854d0e' : '#991b1b',
                        fontWeight: '600'
                      }}>{c.openRate.toFixed(1)}%</span>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>
                      <span style={{ 
                        color: c.clickRate >= 5 ? '#166534' : c.clickRate >= 2 ? '#854d0e' : '#6b7280',
                        fontWeight: '600'
                      }}>{c.clickRate.toFixed(1)}%</span>
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center', color: '#6b7280' }}>
                      {c.clickToOpenRate.toFixed(1)}%
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>
                      {new Date(c.lastSent).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#9ca3af' }}>
          {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''} • Click a row to see detailed analysis
        </div>
      </div>
    );
  }

  return (
    <div>
      <ViewModeToggle />
      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ backgroundColor: '#f3f4f6', borderRadius: '0.5rem', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#374151' }}>{stats.total}</div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Total Sent</div>
        </div>
        <div style={{ backgroundColor: '#dcfce7', borderRadius: '0.5rem', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#166534' }}>{deliveryRate}%</div>
          <div style={{ fontSize: '0.75rem', color: '#166534' }}>Delivery Rate</div>
        </div>
        <div style={{ backgroundColor: '#e0e7ff', borderRadius: '0.5rem', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#4338ca' }}>{openRate}%</div>
          <div style={{ fontSize: '0.75rem', color: '#4338ca' }}>Open Rate</div>
        </div>
        <div style={{ backgroundColor: '#fae8ff', borderRadius: '0.5rem', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#a21caf' }}>{clickRate}%</div>
          <div style={{ fontSize: '0.75rem', color: '#a21caf' }}>Click Rate</div>
        </div>
        <div style={{ backgroundColor: '#fee2e2', borderRadius: '0.5rem', padding: '1rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#991b1b' }}>{bounceRate}%</div>
          <div style={{ fontSize: '0.75rem', color: '#991b1b' }}>Bounce Rate</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
        {/* Date Range */}
        <select
          value={dateRange}
          onChange={(e) => { setDateRange(e.target.value); setPage(1); }}
          style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
        >
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="all">All time</option>
        </select>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
        >
          <option value="all">All statuses</option>
          <option value="sent">Sent</option>
          <option value="delivered">Delivered</option>
          <option value="opened">Opened</option>
          <option value="clicked">Clicked</option>
          <option value="bounced">Bounced</option>
          <option value="spam">Spam</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>

        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '150px', maxWidth: '300px' }}>
          <Search style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', height: '1rem', width: '1rem', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search by email or subject..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            style={{ width: '100%', padding: '0.5rem 1rem 0.5rem 2.5rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', fontSize: '0.875rem', boxSizing: 'border-box' }}
          />
        </div>

        {/* Refresh */}
        <button
          onClick={() => fetchEmailLogs(true)}
          disabled={refreshing}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            padding: '0.5rem 1rem', 
            borderRadius: '0.5rem', 
            border: '1px solid #d1d5db',
            backgroundColor: 'white',
            cursor: refreshing ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem'
          }}
        >
          <RefreshCw style={{ height: '1rem', width: '1rem', animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* Email Logs Table */}
      <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '0.5rem' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Recipient</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Subject</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Status</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Sent</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Opens</th>
              <th style={{ padding: '0.75rem 1rem', textAlign: 'center', fontWeight: '600', color: '#374151', borderBottom: '1px solid #e5e7eb' }}>Clicks</th>
            </tr>
          </thead>
          <tbody>
            {emailLogs.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  No emails found for the selected filters
                </td>
              </tr>
            ) : (
              emailLogs.map((log) => (
                <tr key={log.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <div style={{ fontWeight: '500', color: '#111827' }}>{log.user_email}</div>
                  </td>
                  <td style={{ padding: '0.75rem 1rem', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {log.subject}
                  </td>
                  <td style={{ padding: '0.75rem 1rem' }}>
                    <StatusBadge status={log.status || 'sent'} />
                  </td>
                  <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>
                    {new Date(log.sent_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                    {log.open_count > 0 ? (
                      <span style={{ color: '#4338ca', fontWeight: '500' }}>{log.open_count}</span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '0.75rem 1rem', textAlign: 'center' }}>
                    {log.click_count > 0 ? (
                      <span style={{ color: '#a21caf', fontWeight: '500' }}>{log.click_count}</span>
                    ) : (
                      <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalCount > perPage && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            Showing {((page - 1) * perPage) + 1} - {Math.min(page * perPage, totalCount)} of {totalCount}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <select
              value={perPage}
              onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
              style={{ padding: '0.25rem 0.5rem', borderRadius: '0.25rem', border: '1px solid #d1d5db', fontSize: '0.875rem' }}
            >
              <option value={10}>10 per page</option>
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ 
                padding: '0.5rem', 
                borderRadius: '0.25rem', 
                border: '1px solid #d1d5db',
                backgroundColor: page === 1 ? '#f3f4f6' : 'white',
                cursor: page === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              <ChevronLeft style={{ height: '1rem', width: '1rem' }} />
            </button>
            <span style={{ fontSize: '0.875rem', color: '#374151' }}>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{ 
                padding: '0.5rem', 
                borderRadius: '0.25rem', 
                border: '1px solid #d1d5db',
                backgroundColor: page === totalPages ? '#f3f4f6' : 'white',
                cursor: page === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              <ChevronRight style={{ height: '1rem', width: '1rem' }} />
            </button>
          </div>
        </div>
      )}

      {/* Info about webhook */}
      <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f0fdf4', borderRadius: '0.5rem', border: '1px solid #bbf7d0' }}>
        <p style={{ fontSize: '0.875rem', color: '#166534', margin: 0 }}>
          <strong>📊 Tip:</strong> Email analytics are updated in real-time via Mandrill webhooks. 
          Open and click tracking may take a few minutes to appear after emails are sent.
        </p>
      </div>
    </div>
  );
};

export default EmailAnalytics;
